import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { inr, csvRow } from "@/lib/utils2";
import { Search, ArrowRight, Download } from "lucide-react";

export default function PartyLedgers() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = usePersistedState("q:party-ledgers", "");

  useEffect(() => { (async () => setRows((await api.get("/dashboard/party-summary")).data))(); }, []);

  const filtered = rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

  const exportCsv = () => {
    const headers = ["Party", "Debit (Billed)", "Credit (Received)", "Balance"];
    const data = filtered.map((r) => [r.name, r.debit, r.credit, r.balance]);
    const csv = [headers, ...data].map(csvRow).join("");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `tulsi-party-ledgers-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const totals = filtered.reduce((a, r) => ({
    debit: a.debit + r.debit, credit: a.credit + r.credit, balance: a.balance + r.balance,
  }), { debit: 0, credit: 0, balance: 0 });

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-5xl mx-auto" data-testid="party-ledgers-page">
      <PageHeader
        title="Party Ledgers"
        subtitle="Outstanding balances by party. Click any row for the full bill-wise + payment-wise ledger."
        action={<Button onClick={exportCsv} data-testid="export-party-ledgers-btn" className="rounded-sm bg-brand-forest text-white"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>}
      />

      <Card className="rounded-sm border-brand-line shadow-none p-4 mb-5">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search party…" className="pl-9 h-10 rounded-sm bg-white" data-testid="party-search" />
        </div>
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-3">Party</th>
              <th className="text-right px-3 py-3">Debit (Billed)</th>
              <th className="text-right px-3 py-3">Credit (Received)</th>
              <th className="text-right px-3 py-3">Balance</th>
              <th className="px-5 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-brand-muted">No parties.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.party_id} className="border-t border-brand-line hover:bg-secondary/40 cursor-pointer"
                onClick={() => nav(`/ledger/party/${r.party_id}`)}
                data-testid={`party-ledger-row-${r.party_id}`}>
                <td className="px-5 py-3 font-medium">{r.name}</td>
                <td className="px-3 py-3 text-right tabular-nums">{inr(r.debit)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-brand-forest">{inr(r.credit)}</td>
                <td className={`px-3 py-3 text-right tabular-nums font-medium ${r.balance > 0 ? "text-brand-terracotta" : "text-brand-muted"}`}>{inr(r.balance)}</td>
                <td className="px-5 py-3">
                  <Link to={`/ledger/party/${r.party_id}`} className="inline-flex items-center text-brand-forest"><ArrowRight className="h-4 w-4" /></Link>
                </td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-secondary/40 text-sm border-t-2 border-brand-ink">
              <tr>
                <td className="px-5 py-3 uppercase text-[10px] tracking-[0.15em] text-brand-muted">Totals</td>
                <td className="px-3 py-3 text-right tabular-nums font-semibold">{inr(totals.debit)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-brand-forest font-semibold">{inr(totals.credit)}</td>
                <td className="px-3 py-3 text-right tabular-nums font-semibold">{inr(totals.balance)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>
    </div>
  );
}
